import { Modal } from "antd";
import dayjs from "dayjs";
import { memo } from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Header from "../components/Header.js";
import Legend from "../components/Legend.js";
import Login from "../components/Login.js";
import MyMap from "../components/MyMap.js";
import Plot from "../components/Plot.js";
import PlotHeader from "../components/PlotHeader.js";
import Settings from "../components/Settings.js";
import buildAlertsPlotData from "../js/buildAlertsPlotData.js";
import buildJamsPlotData from "../js/buildJamsPlotData.js";
import { yAxisOptionsValueToLabel } from "../js/constants.js";

const Home = memo(({ backendUrl, token, setToken, state }) => {
  // the following variables DO NOT depend on searchParams
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [geojsonData, setGeojsonData] = useState(null);
  const [plotData, setPlotData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTime, setLoadingTime] = useState();
  const [hasFetchFailed, setHasFetchFailed] = useState(false);
  const [eventsCount, setEventsCount] = useState();
  const [recordsCount, setRecordsCount] = useState();
  const [congestionRate, setCongestionRate] = useState(null);
  const [dataCompletenessRate, setDataCompletenessRate] = useState();
  const [hoveredFeatureId, setHoveredFeatureId] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  // the following variables DO depend on searchParams
  // -- time settings
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  let initialFrom;
  let initialTo;
  if (fromParam && toParam) {
    initialFrom = dayjs(fromParam, "YYYY-MM-DD")
      .set("hour", 0)
      .set("minute", 0)
      .set("second", 0)
      .format();
    initialTo = dayjs(toParam, "YYYY-MM-DD")
      .set("hour", 23)
      .set("minute", 59)
      .set("second", 59)
      .format();
  } else {
    const yesterday = dayjs().subtract(1, "day");
    initialFrom = yesterday
      .set("hour", 0)
      .set("minute", 0)
      .set("second", 0)
      .format();
    initialTo = yesterday
      .set("hour", 23)
      .set("minute", 59)
      .set("second", 59)
      .format();
  }
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);

  const [selectedDays, setSelectedDays] = useState(
    searchParams
      .get("selected_days")
      ?.split(",")
      .map((x) => Number.parseInt(x)) || [0, 1, 2, 3, 4, 5, 6],
  );
  const [selectedHours, setSelectedHours] = useState(
    searchParams
      .get("selected_hours")
      ?.split(",")
      .map((x) => Number.parseInt(x)) || [...Array(24).keys()],
  );
  // -- dataset selection
  const showAlertsParam = searchParams.get("show_alerts");
  const [areAlertsVisible, setAreAlertsVisible] = useState(
    showAlertsParam ? showAlertsParam === "true" : false,
  );
  const showJamsParam = searchParams.get("show_jams");
  const [areJamsVisible, setAreJamsVisible] = useState(
    showJamsParam ? showJamsParam === "true" : true,
  );
  const showIrregularitiesParam = searchParams.get("show_irregularities");
  const [areIrregularitiesVisible, setAreIrregularitiesVisible] = useState(
    showIrregularitiesParam ? showIrregularitiesParam === "true" : false,
  );
  // -- map
  const [zoom, setZoom] = useState(
    Number.parseFloat(searchParams.get("zoom")) || 10.6,
  );
  const [lng, setLng] = useState(
    Number.parseFloat(searchParams.get("lng")) || 6.16,
  );
  const [lat, setLat] = useState(
    Number.parseFloat(searchParams.get("lat")) || 46.22,
  );
  const [colorBy, setColorBy] = useState(
    searchParams.get("color_by") || "congestion_rate",
  );
  const [roadNetwork, setRoadNetwork] = useState(
    searchParams.get("road_network") || "graphe_routier_grand_geneve",
  );
  // -- filters
  const maxAngleDegreesParam = searchParams.get("max_angle_degrees");
  const [maxAngleDegrees, setMaxAngleDegrees] = useState(
    maxAngleDegreesParam ? Number.parseInt(maxAngleDegreesParam) : 30,
  );
  const minOverlapPercentageParam = searchParams.get("min_overlap_percentage");
  const [minOverlapPercentage, setMinOverlapPercentage] = useState(
    minOverlapPercentageParam ? Number.parseInt(minOverlapPercentageParam) : 50,
  );
  const minCongestionCoefficientParam = searchParams.get(
    "min_congestion_coefficient",
  );
  const [minCongestionCoefficient, setMinCongestionCoefficient] = useState(
    minCongestionCoefficientParam
      ? Number.parseFloat(minCongestionCoefficientParam)
      : 0.0,
  );
  // -- plot
  const [alertsPlotYAxisVar, setAlertsPlotYAxisVar] = useState(
    searchParams.get("alerts_plot_y_axis_var") || "reliability",
  );
  const [
    jamsOrIrregularitiesPlotYAxisVar,
    setJamsOrIrregularitiesPlotYAxisVar,
  ] = useState(
    searchParams.get("jams_or_irregularities_plot_y_axis_var") ||
      "congestion_coefficient",
  );
  // -- user interaction
  const [selectedRoadSegmentGeomMd5, setSelectedRoadSegmentGeomMd5] = useState(
    searchParams.get("selected_road_segment_geom_md5"),
  );
  const [selectedAlertGeomMd5, setSelectedAlertGeomMd5] = useState(
    searchParams.get("selected_alert_geom_md5"),
  );

  const selectedFeatureParam = searchParams.get("selected_feature");

  let initialSelectedFeature = null;

  if (selectedFeatureParam) {
    const [
      _selectedFeatureId,
      _selectedFeatureX,
      _selectedFeatureY,
      _selectedFeatureColor,
    ] = selectedFeatureParam.split(",");

    initialSelectedFeature = {
      id: _selectedFeatureId,
      x: _selectedFeatureX,
      y: _selectedFeatureY,
      color: _selectedFeatureColor,
    };
  }

  const [selectedFeature, setSelectedFeature] = useState(
    initialSelectedFeature,
  );

  const CONGESTION_RATE_BIN_WIDTH_MINUTES = 1;

  // keep searchParams updated
  useEffect(() => {
    let _searchParams = {
      zoom,
      lng,
      lat,
      from: dayjs(from).format("YYYY-MM-DD"),
      to: dayjs(to).format("YYYY-MM-DD"),
      selected_days: selectedDays.join(","),
      selected_hours: selectedHours.join(","),
      show_alerts: JSON.stringify(areAlertsVisible),
      show_jams: JSON.stringify(areJamsVisible),
      show_irregularities: JSON.stringify(areIrregularitiesVisible),
      color_by: colorBy,
      road_network: roadNetwork,
      min_congestion_coefficient: minCongestionCoefficient,
      max_angle_degrees: maxAngleDegrees,
      min_overlap_percentage: minOverlapPercentage,
      alerts_plot_y_axis_var: alertsPlotYAxisVar,
      jams_or_irregularities_plot_y_axis_var: jamsOrIrregularitiesPlotYAxisVar,
    };

    if (selectedAlertGeomMd5) {
      _searchParams = {
        ..._searchParams,
        selected_alert_geom_md5: selectedAlertGeomMd5,
      };
    }

    if (selectedRoadSegmentGeomMd5) {
      _searchParams = {
        ..._searchParams,
        selected_road_segment_geom_md5: selectedRoadSegmentGeomMd5,
      };
    }

    if (selectedFeature?.id) {
      _searchParams = {
        ..._searchParams,
        selected_feature: `${selectedFeature.id},${selectedFeature.x},${selectedFeature.y},${selectedFeature.color}`,
      };
    }

    setSearchParams(_searchParams);
  }, [
    setSearchParams,
    zoom,
    lng,
    lat,
    from,
    to,
    selectedDays,
    selectedHours,
    areAlertsVisible,
    areJamsVisible,
    areIrregularitiesVisible,
    colorBy,
    roadNetwork,
    minCongestionCoefficient,
    maxAngleDegrees,
    minOverlapPercentage,
    selectedAlertGeomMd5,
    selectedRoadSegmentGeomMd5,
    alertsPlotYAxisVar,
    jamsOrIrregularitiesPlotYAxisVar,
    selectedFeature,
  ]);

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  // check token
  useEffect(() => {
    const checkToken = async () => {
      const response = await fetch(`${backendUrl}/checktoken`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsTokenValid(response.ok);
    };

    checkToken();
  }, [token, backendUrl]);

  // display jam / irregularity features
  useEffect(() => {
    if (
      selectedRoadSegmentGeomMd5 === null ||
      selectedHours.length === 0 ||
      selectedDays.length === 0
    ) {
      setPlotData([]);
      return;
    }

    // see https://devtrium.com/posts/async-functions-useeffect
    const fetchData = async () => {
      setHasFetchFailed(false);
      setIsLoading(true);
      const time1 = performance.now();

      const path = areJamsVisible ? "jams" : "irregularities";
      // get the data from the api
      const eventsResponse = await fetch(
        `${backendUrl}/${path}/events?road_network=${roadNetwork}&geometry_md5=${selectedRoadSegmentGeomMd5}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&max_angle_degrees=${encodeURIComponent(maxAngleDegrees)}&min_overlap=${encodeURIComponent(minOverlapPercentage / 100.0)}&min_congestion_coefficient=${encodeURIComponent(minCongestionCoefficient)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}&bin_width_minutes=${encodeURIComponent(CONGESTION_RATE_BIN_WIDTH_MINUTES)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const geometriesResponse = await fetch(
        `${backendUrl}/${path}/geometries?road_network=${roadNetwork}&geometry_md5=${selectedRoadSegmentGeomMd5}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&max_angle_degrees=${encodeURIComponent(maxAngleDegrees)}&min_overlap=${encodeURIComponent(minOverlapPercentage / 100.0)}&min_congestion_coefficient=${encodeURIComponent(minCongestionCoefficient)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const time2 = performance.now();
      setLoadingTime(time2 - time1);

      if (!eventsResponse.ok | !geometriesResponse.ok) {
        setHasFetchFailed(true);
        throw new Error("Could not fetch JSON data");
      }

      const events = await eventsResponse.json();

      const geometries = await geometriesResponse.json();

      return { events, geometries };
    };

    fetchData()
      .then(({ events, geometries }) => {
        setGeojsonData(geometries);
        if (events) {
          setRecordsCount(events.record_count);
          setEventsCount(events.unique_event_count);
          setCongestionRate(events.congestion_rate);
          const _plotData = buildJamsPlotData(
            events.records,
            jamsOrIrregularitiesPlotYAxisVar,
          );
          setPlotData(_plotData);
        } else {
          setPlotData([]);
          setEventsCount(undefined);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setPlotData([]);
        setEventsCount(undefined);
        setLoadingTime();
        setIsLoading(false);
        if (error.message === "403") {
          setToken(); // logout
        }
      });
  }, [
    selectedRoadSegmentGeomMd5,
    from,
    to,
    backendUrl,
    token,
    setToken,
    areJamsVisible,
    maxAngleDegrees,
    minOverlapPercentage,
    minCongestionCoefficient,
    jamsOrIrregularitiesPlotYAxisVar,
    selectedDays,
    selectedHours,
    roadNetwork,
  ]);

  // display alert features
  useEffect(() => {
    if (
      selectedAlertGeomMd5 === null ||
      selectedHours.length === 0 ||
      selectedDays.length === 0
    ) {
      setPlotData([]);
      return;
    }

    // see https://devtrium.com/posts/async-functions-useeffect
    const fetchData = async () => {
      setHasFetchFailed(false);
      setIsLoading(true);
      const time1 = performance.now();
      // get the data from the api
      const eventsResponse = await fetch(
        `${backendUrl}/alerts/events?geometry_md5=${selectedAlertGeomMd5}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const time2 = performance.now();
      setLoadingTime(time2 - time1);

      if (!eventsResponse.ok) {
        setHasFetchFailed(true);
        throw new Error("Could not fetch JSON data");
      }

      const events = await eventsResponse.json();

      return { events };
    };

    fetchData()
      .then(({ events }) => {
        setGeojsonData([]);
        if (events) {
          setRecordsCount(events.record_count);
          setEventsCount(events.unique_event_count);
          const _plotData = buildAlertsPlotData(
            events.records,
            alertsPlotYAxisVar,
          );
          setPlotData(_plotData);
          setCongestionRate(null);
        } else {
          setPlotData([]);
          setEventsCount(undefined);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        if (error.message === "403") {
          setToken(); // logout
        }
      });
  }, [
    selectedAlertGeomMd5,
    from,
    to,
    backendUrl,
    token,
    setToken,
    alertsPlotYAxisVar,
    selectedDays,
    selectedHours,
  ]);

  // get data quality
  useEffect(() => {
    if (selectedHours.length === 0 || selectedDays.length === 0) {
      setDataCompletenessRate(0);
      return;
    }
    const fetchDataQuality = async () => {
      const dataQualityResponse = await fetch(
        `${backendUrl}/data-quality?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!dataQualityResponse.ok) {
        throw new Error("Could not get data quality information");
      }

      const dataQuality = await dataQualityResponse.json();

      return { dataQuality };
    };

    fetchDataQuality()
      .then(({ dataQuality }) => {
        setDataCompletenessRate(dataQuality.completenessRate);
      })
      .catch((error) => {
        if (error.message === "403") {
          setToken(); // logout
        }
      });
  }, [from, to, backendUrl, token, setToken, selectedDays, selectedHours]);

  if (!token || !isTokenValid) {
    return (
      <div
        style={{
          top: "50%",
          left: "50%",
          transform: "translate3d(-50%,-50%, 0)",
          position: "absolute",
        }}
      >
        <Login backendUrl={backendUrl} state={state} />
      </div>
    );
  }

  return (
    <div className="App">
      <Modal
        title="À propos de cette application"
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
      >
        <p>[...]</p>
        <h3>Crédits</h3>
        <ul>
          <li>
            <a href="https://www.flaticon.com/authors/freepik">
              Icônes crées par Freepik - Flaticon
            </a>
          </li>
          <li>
            <a href="https://realfavicongenerator.net">
              Favicon générée par RealFaviconGenerator
            </a>
          </li>
        </ul>
      </Modal>

      <Header
        setIsModalOpen={setIsModalOpen}
        setToken={setToken}
        from={from}
        setFrom={setFrom}
        to={to}
        setTo={setTo}
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        selectedHours={selectedHours}
        setSelectedHours={setSelectedHours}
        completenessRate={dataCompletenessRate}
      />

      <Plot
        plotData={plotData}
        from={from}
        to={to}
        yaxisTitle={
          selectedRoadSegmentGeomMd5
            ? yAxisOptionsValueToLabel[jamsOrIrregularitiesPlotYAxisVar]
            : yAxisOptionsValueToLabel[alertsPlotYAxisVar]
        }
        setHoveredFeatureId={setHoveredFeatureId}
        selectedFeature={selectedFeature}
        setSelectedFeature={setSelectedFeature}
        isFeatureSelected={selectedRoadSegmentGeomMd5 || selectedAlertGeomMd5}
        isLoading={isLoading}
        hasFetchFailed={hasFetchFailed}
      />

      <PlotHeader
        isLoading={isLoading}
        hasPlotData={plotData.length > 0}
        alertsPlotYAxisVar={alertsPlotYAxisVar}
        setAlertsPlotYAxisVar={setAlertsPlotYAxisVar}
        jamsOrIrregularitiesPlotYAxisVar={jamsOrIrregularitiesPlotYAxisVar}
        setJamsOrIrregularitiesPlotYAxisVar={
          setJamsOrIrregularitiesPlotYAxisVar
        }
        recordsCount={recordsCount}
        eventsCount={eventsCount}
        congestionRate={congestionRate}
        hasFetchFailed={hasFetchFailed}
        loadingTime={loadingTime}
        isAlertSelected={selectedAlertGeomMd5 != null}
        isRoadSegmentSelected={selectedRoadSegmentGeomMd5 !== null}
      />

      <Settings
        areAlertsVisible={areAlertsVisible}
        setAreAlertsVisible={setAreAlertsVisible}
        areJamsVisible={areJamsVisible}
        setAreJamsVisible={setAreJamsVisible}
        areIrregularitiesVisible={areIrregularitiesVisible}
        setAreIrregularitiesVisible={setAreIrregularitiesVisible}
        colorBy={colorBy}
        setColorBy={setColorBy}
        roadNetwork={roadNetwork}
        setRoadNetwork={setRoadNetwork}
        maxAngleDegrees={maxAngleDegrees}
        setMaxAngleDegrees={setMaxAngleDegrees}
        minOverlapPercentage={minOverlapPercentage}
        setMinOverlapPercentage={setMinOverlapPercentage}
        minCongestionCoefficient={minCongestionCoefficient}
        setMinCongestionCoefficient={setMinCongestionCoefficient}
      />

      <MyMap
        zoom={zoom}
        setZoom={setZoom}
        lng={lng}
        setLng={setLng}
        lat={lat}
        setLat={setLat}
        geojsonData={geojsonData}
        selectedRoadSegmentGeomMd5={selectedRoadSegmentGeomMd5}
        setSelectedRoadSegmentGeomMd5={setSelectedRoadSegmentGeomMd5}
        selectedAlertGeomMd5={selectedAlertGeomMd5}
        setSelectedAlertGeomMd5={setSelectedAlertGeomMd5}
        hoveredFeatureId={hoveredFeatureId}
        selectedFeatureId={
          selectedFeature?.id && selectedFeature.id ? selectedFeature.id : null
        }
        backendUrl={backendUrl}
        backendToken={token}
        from={from}
        to={to}
        areAlertsVisible={areAlertsVisible}
        jamsOrIrregularities={areJamsVisible ? "jams" : "irregularities"}
        colorBy={colorBy}
        roadNetwork={roadNetwork}
        maxAngleDegrees={maxAngleDegrees}
        minOverlap={minOverlapPercentage / 100.0}
        minCongestionCoefficient={minCongestionCoefficient}
        congestionRateBinWidthMinutes={CONGESTION_RATE_BIN_WIDTH_MINUTES}
        selectedDays={selectedDays}
        selectedHours={selectedHours}
      />

      <Legend colorBy={colorBy} areAlertsVisible={areAlertsVisible} />
    </div>
  );
});

export default Home;
